import { useConfig } from "@/context/use-context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import API from "./api";

interface AlertResponse {
	energy: "ELEC" | "GAS";
	alert_type: string;
	value: number;
	unit: string;
}

interface UpdateAlertConsumptionParams {
	alert_type: "USAGE_LIMIT_CONSUMPTION_MONTHLY";
	alert_id: string;
	energy: "ELEC" | "GAS";
	is_active: boolean;
	value: number;
	unit: "kWh";
}

interface UpdateAlertCostParams {
	alert_type: "USAGE_LIMIT_COST_MONTHLY";
	alert_id: string;
	energy: "ELEC" | "GAS";
	is_active: boolean;
	value: number;
	unit: "EUR";
}

interface UpdateAlertDataAvailableParams {
	alert_type: "DATA_AVAILABLE";
	alert_id: string;
	energy: "ELEC" | "GAS";
	is_active: boolean;
}

export type UpdateAlertParams =
	| UpdateAlertConsumptionParams
	| UpdateAlertCostParams
	| UpdateAlertDataAvailableParams;

export const updateAlert = async (
	args: UpdateAlertParams & {
		BEARER_TOKEN: string;
		POD: string;
	},
): Promise<AlertResponse> => {
	const response = await API.put(
		`/pods/${args.POD}/alerts/${args.alert_id}`,
		{
			energy: args.energy,
			alert_type: args.alert_type,
			is_active: args.is_active,
			...(args.alert_type !== "DATA_AVAILABLE"
				? { value: args.value, unit: args.unit }
				: {}),
		},
		{
			headers: {
				Authorization: `Bearer ${args.BEARER_TOKEN}`,
			},
		},
	);

	return response.data;
};

// React Query Hook
export const useUpdateAlert = () => {
	const config = useConfig();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: UpdateAlertParams) =>
			updateAlert({
				...params,
				BEARER_TOKEN: config.bearerToken,
				POD: config.pod?.pod_id ?? "",
			}),
		onSuccess: () => {
			// Invalidate and refetch alerts data
			queryClient.invalidateQueries({ queryKey: ["alertData"] });
		},
		onError: (error) => {
			console.error("Error in updateAlert mutation:", error);
		},
	});
};
